import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { useParams, useSearchParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, setDoc, Timestamp, where } from 'firebase/firestore';
import { getFirestore } from '@firebase/firestore';
import { app } from '../firebase';
import { ORGANIZATION_NAME } from '../branding';
import './dash.css';
import { getInterviewJoinAvailability, INTERVIEW_JOIN_EARLY_WINDOW_MINUTES } from '../utils/interviewScheduling';

const DAILY_API_KEY = process.env.REACT_APP_DAILY_API_KEY || '9ca6078bdb4b21b14f89f74bf52454758a58e2488b6ce5a04e640014f9fc5c7c';

const getDateObject = (value) => {
  if (!value) return null;
  const dateObj = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return null;
  return dateObj;
};

const formatInviteDateTime = (value) => {
  const dateObj = getDateObject(value);
  if (!dateObj) return 'Not scheduled';

  return dateObj.toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Africa/Nairobi',
    timeZoneName: 'short'
  });
};

function InterviewMeetingPage() {
  const { meetingId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const db = getFirestore(app);
  const auth = getAuth();

  const [currentUser, setCurrentUser] = useState(null);
  const [currentAdminName, setCurrentAdminName] = useState('');
  const [meetingRecord, setMeetingRecord] = useState(null);
  const [meetingUrl, setMeetingUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const leftMeeting = searchParams.get('left') === '1';
  const [currentTime, setCurrentTime] = useState(() => new Date());

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const loadCurrentAdminIdentity = async () => {
      if (!currentUser) {
        setCurrentAdminName('');
        return;
      }

      const fallbackName = currentUser.displayName || currentUser.email || 'Admin';

      try {
        const directDoc = await getDoc(doc(db, 'Admins', currentUser.uid));
        if (directDoc.exists()) {
          setCurrentAdminName(directDoc.data().userName || fallbackName);
          return;
        }

        const byUserId = await getDocs(query(collection(db, 'Admins'), where('userID', '==', currentUser.uid)));
        if (!byUserId.empty) {
          setCurrentAdminName(byUserId.docs[0].data().userName || fallbackName);
          return;
        }

        if (currentUser.email) {
          const byEmail = await getDocs(query(collection(db, 'Admins'), where('userEmail', '==', currentUser.email)));
          if (!byEmail.empty) {
            setCurrentAdminName(byEmail.docs[0].data().userName || fallbackName);
            return;
          }
        }

        setCurrentAdminName(fallbackName);
      } catch (identityError) {
        console.error('Failed to resolve admin identity for public interview page:', identityError);
        setCurrentAdminName(fallbackName);
      }
    };

    loadCurrentAdminIdentity();
  }, [currentUser, db]);

  useEffect(() => {
    const loadMeetingRecord = async () => {
      if (!meetingId) {
        setError('The meeting link is missing a meeting ID.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        let matchedDoc = null;
        const byMeetingId = await getDocs(query(collection(db, 'interviewCalls'), where('meetingId', '==', meetingId)));
        if (!byMeetingId.empty) {
          matchedDoc = byMeetingId.docs[0];
        } else {
          const byRoomName = await getDocs(query(collection(db, 'interviewCalls'), where('roomName', '==', meetingId)));
          if (!byRoomName.empty) {
            matchedDoc = byRoomName.docs[0];
          }
        }

        if (!matchedDoc) {
          setMeetingRecord(null);
          setError('We could not find an interview for this link.');
          setLoading(false);
          return;
        }

        setMeetingRecord({ id: matchedDoc.id, ...matchedDoc.data() });
      } catch (meetingError) {
        console.error('Failed to load interview meeting by meeting ID:', meetingError);
        setError('Unable to load this interview right now.');
      } finally {
        setLoading(false);
      }
    };

    loadMeetingRecord();
  }, [db, meetingId]);

  const createParticipantMeetingUrl = useCallback(async (record) => {
    if (!record?.roomName || !record?.roomUrl) {
      return '';
    }

    const participantIsAdmin = !!currentUser;
    const participantName = participantIsAdmin
      ? (currentAdminName || currentUser?.displayName || currentUser?.email || 'Admin')
      : (record.studentName || 'Student');

    const participantId = participantIsAdmin
      ? (currentUser?.uid || record.userId || record.roomName)
      : `student-${record.userId || record.id || record.roomName}`;

    const redirectUrl = typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}?left=1`
      : undefined;

    const response = await fetch('https://api.daily.co/v1/meeting-tokens', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${DAILY_API_KEY}`
      },
      body: JSON.stringify({
        properties: {
          room_name: record.roomName,
          is_owner: participantIsAdmin,
          user_name: participantName,
          user_id: participantId,
          enable_prejoin_ui: true,
          redirect_on_meeting_exit: redirectUrl
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Daily meeting token creation failed with status ${response.status}`);
    }

    const tokenData = await response.json();
    if (!tokenData?.token) {
      return record.roomUrl;
    }

    return `${record.roomUrl}${record.roomUrl.includes('?') ? '&' : '?'}t=${encodeURIComponent(tokenData.token)}`;
  }, [currentAdminName, currentUser]);

  useEffect(() => {
    const buildJoinUrl = async () => {
      if (leftMeeting) {
        setMeetingUrl('');
        setJoining(false);
        return;
      }

      if (!meetingRecord?.roomUrl || !meetingRecord?.roomName) {
        setMeetingUrl('');
        return;
      }

      if (meetingRecord.status === 'ended') {
        setMeetingUrl('');
        return;
      }

      const joinAvailability = meetingRecord?.status === 'scheduled'
        ? getInterviewJoinAvailability(meetingRecord?.scheduledFor, currentTime)
        : { canJoin: true };

      if (meetingRecord?.status === 'scheduled' && !joinAvailability.canJoin) {
        setMeetingUrl('');
        setJoining(false);
        return;
      }

      setJoining(true);
      try {
        const tokenizedUrl = await createParticipantMeetingUrl(meetingRecord);
        setMeetingUrl(tokenizedUrl || meetingRecord.roomUrl);

        if (currentUser) {
          const now = Timestamp.now();
          await setDoc(doc(db, 'interviewCalls', meetingRecord.id), {
            status: 'active',
            startedAt: meetingRecord.startedAt || now,
            hostJoinedAt: meetingRecord.hostJoinedAt || now,
            hostJoinedBy: currentUser.uid || null,
            hostJoinedName: currentAdminName || currentUser.displayName || currentUser.email || 'Admin',
            hostJoinedEmail: currentUser.email || '',
            bothJoinedAt: meetingRecord.bothJoinedAt || (meetingRecord?.studentJoinedAt ? now : null),
            lastJoinedAt: now,
            lastJoinedBy: currentUser.uid || null,
            updatedAt: now
          }, { merge: true });
          setMeetingRecord((prev) => (
            prev ? {
              ...prev,
              status: 'active',
              startedAt: prev.startedAt || now,
              hostJoinedAt: prev.hostJoinedAt || now,
              hostJoinedBy: currentUser.uid || null,
              hostJoinedName: currentAdminName || currentUser.displayName || currentUser.email || 'Admin',
              hostJoinedEmail: currentUser.email || '',
              bothJoinedAt: prev.bothJoinedAt || (prev?.studentJoinedAt ? now : null),
              lastJoinedAt: now,
              updatedAt: now
            } : prev
          ));
        } else {
          const now = Timestamp.now();
          await setDoc(doc(db, 'interviewCalls', meetingRecord.id), {
            status: 'active',
            startedAt: meetingRecord.startedAt || now,
            studentJoinedAt: meetingRecord.studentJoinedAt || now,
            studentJoinedBy: meetingRecord.userId || null,
            studentJoinedName: meetingRecord.studentName || 'Student',
            studentJoinedEmail: meetingRecord.studentEmail || '',
            bothJoinedAt: meetingRecord.bothJoinedAt || (meetingRecord?.hostJoinedAt ? now : null),
            lastJoinedAt: now,
            lastJoinedBy: meetingRecord.userId || null,
            updatedAt: now
          }, { merge: true });
          setMeetingRecord((prev) => (
            prev ? {
              ...prev,
              status: 'active',
              startedAt: prev.startedAt || now,
              studentJoinedAt: prev.studentJoinedAt || now,
              studentJoinedBy: prev.userId || null,
              studentJoinedName: prev.studentName || 'Student',
              studentJoinedEmail: prev.studentEmail || '',
              bothJoinedAt: prev.bothJoinedAt || (prev?.hostJoinedAt ? now : null),
              lastJoinedAt: now,
              lastJoinedBy: prev.userId || null,
              updatedAt: now
            } : prev
          ));
        }
      } catch (joinError) {
        console.error('Failed to prepare meeting URL:', joinError);
        setMeetingUrl(meetingRecord.roomUrl || '');
      } finally {
        setJoining(false);
      }
    };

    buildJoinUrl();
  }, [createParticipantMeetingUrl, currentUser, currentTime, db, leftMeeting, meetingRecord]);
  const statusLabel = useMemo(() => {
    if (!meetingRecord?.status) return 'Interview ready';
    if (meetingRecord.status === 'scheduled') return 'Scheduled interview';
    if (meetingRecord.status === 'active') return 'Interview in progress';
    if (meetingRecord.status === 'ended') return 'Interview ended';
    return 'Interview ready';
  }, [meetingRecord]);

  if (loading) {
    return (
      <div className="interviewMeetingPage">
        <div className="interviewMeetingShell">
          <div className="interviewMeetingLoading">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="interviewMeetingPage">
        <div className="interviewMeetingShell">
          <div className="interviewMeetingEmptyState">
            <p className="interviewMeetingKicker">Interview link</p>
            <h2>Unable to open this meeting</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!meetingRecord) {
    return null;
  }

  const joinAvailability = meetingRecord?.status === 'scheduled'
    ? getInterviewJoinAvailability(meetingRecord?.scheduledFor, currentTime)
    : { canJoin: true, availableAt: null };

  return (
    <div className="interviewMeetingPage">
      <div className="interviewMeetingShell">
        <div className="interviewMeetingHeader">
          <div>
            <p className="interviewMeetingKicker">{ORGANIZATION_NAME}</p>
            <h2>{meetingRecord.studentName || 'Interview meeting'}</h2>
            <p className="interviewMeetingSubtext">
              {meetingRecord.course || 'Course not set'} • {meetingRecord.nationality || 'Country not set'}
            </p>
          </div>
          <div className="interviewMeetingMeta">
            <span>{statusLabel}</span>
            <span>{formatInviteDateTime(meetingRecord.scheduledFor || meetingRecord.startedAt)}</span>
          </div>
        </div>

        {leftMeeting && meetingRecord.status !== 'ended' && (
          <div className="interviewMeetingNotice">
            You left the meeting. Rejoin any time using this page until the interview is ended by the team.
          </div>
        )}

        {meetingRecord.status === 'scheduled' && !joinAvailability.canJoin && (
          <div className="interviewMeetingNotice interviewMeetingNotice--scheduled">
            This interview opens {INTERVIEW_JOIN_EARLY_WINDOW_MINUTES} minutes before the scheduled time. You can join at {formatInviteDateTime(joinAvailability.availableAt)}.
          </div>
        )}

        {meetingRecord.status === 'ended' ? (
          <div className="interviewMeetingEmptyState">
            <p className="interviewMeetingKicker">Interview closed</p>
            <h2>This interview has ended</h2>
            <p>{`Please contact ${ORGANIZATION_NAME} if you need a new invite.`}</p>
          </div>
        ) : (
          <div className="interviewMeetingFrameCard">
            <div className="interviewMeetingFrameMeta">
              <span>Meeting ID</span>
              <strong>{meetingRecord.meetingId || meetingRecord.roomName}</strong>
            </div>
            <div className="interviewMeetingFrameMeta">
              <span>Join link</span>
              <strong>{meetingRecord.meetingLink || (typeof window !== 'undefined' ? window.location.href : '')}</strong>
            </div>
            <div className="interviewMeetingFrameWrap">
              {joining ? (
                <div className="interviewMeetingLoading">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Joining...</span>
                  </Spinner>
                </div>
              ) : meetingRecord.status === 'scheduled' && !joinAvailability.canJoin ? (
                <div className="interviewMeetingLoading interviewMeetingRejoinState">
                  <div>
                    <p className="interviewMeetingKicker">Interview schedule</p>
                    <h3>Meeting not open yet</h3>
                    <p>The room becomes available at {formatInviteDateTime(joinAvailability.availableAt)}.</p>
                  </div>
                </div>
              ) : leftMeeting ? (
                <div className="interviewMeetingLoading interviewMeetingRejoinState">
                  <div>
                    <p className="interviewMeetingKicker">Interview link</p>
                    <h3>Ready when you are</h3>
                    <p>Rejoin the same room whenever you are ready.</p>
                    <button
                      className="interviewCallBtn"
                      onClick={() => window.location.assign(window.location.pathname)}
                    >
                      Rejoin meeting
                    </button>
                  </div>
                </div>
              ) : (
                <iframe
                  src={meetingUrl}
                  title={`Interview for ${meetingRecord.studentName || 'applicant'}`}
                  className="interviewMeetingFrame"
                  allow="camera; microphone; fullscreen; speaker-selection; display-capture; autoplay; clipboard-write"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default InterviewMeetingPage;
